import * as React from "react";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { NationalIdentifierPair } from "./NationalIdentifierPair";
import { CustomerTaxResidencyModel } from "../../../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { CountryCodeMap } from "../../../../../../Shared/Entities/Maps/CountryCodeMap";
import { MifirCountryIdentifier } from "../../../../../../../Wrap/Models/Domains/Regulations/MifirCountryIdentifier";
import { MifirCountryIdentifierClient } from "../../../../../../../Wrap/Api/Regulations/MifirCountryIdentifier";
import { MifirIdentifierType } from "../../../../../../../Wrap/Constants/Enums/Domain/MifirIdentifierType";

interface INationalityProps extends IProcessComponentProps {
    data: CustomerTaxResidencyModel[];
    disableTax: boolean;
    residencies: CustomerTaxResidencyModel[];
    onChange: (customerTaxResidencyModel: CustomerTaxResidencyModel[]) => void;
    isPoa?: boolean;
}

interface INationalityState {
    mifirCountryIdentifiers: Map<CountryCode, MifirCountryIdentifier[]>;
}

export class Nationality extends ProcessComponent<INationalityProps, INationalityState> {
    constructor(props: INationalityProps) {
        super(props);
        this.state = {
            mifirCountryIdentifiers: new Map<CountryCode, MifirCountryIdentifier[]>(),
        };
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        this.props.data?.map((x) => x.Country && this.loadMifirCountryIdentifier(x.Country));
    }

    private addCountryComponent(): void {
        const { data, onChange } = this.props;
        const changes = [...data];
        changes.push({
            IsNationality: true,
            Country: CountryCode.NotSpecified
        });
        onChange(changes);
    }

    private removeCountryComponent(index: number): void {
        const { data, onChange } = this.props;
        const changes = [...data];
        changes.splice(index, 1);
        onChange(changes);
    }

    private countryHasTaxReferenceIdentifier(isTaxResident: boolean, country: CountryCode): boolean {
        const { mifirCountryIdentifiers } = this.state;
        return (
            country &&
            isTaxResident &&
            mifirCountryIdentifiers.has(country) &&
            mifirCountryIdentifiers.get(country)[0].IdentifierType == MifirIdentifierType.TaxNumber
        );
    }

    render() {
        const { data, onChange, disableTax, residencies } = this.props;

        if (data && data.length === 0) {
            data.push({ IsNationality: true, Country: CountryCode.NotSpecified });
        }

        return (
            <>
                {data &&
                    data.map((nationality, index) => {
                        const isTaxResident =
                            nationality.Country === CountryCode.POL
                                ? false
                                : residencies.some((residency) => {
                                      return residency.Country === nationality.Country;
                                  });

                        return (
                            <div key={`nationality-${index}`}>
                                <div className="row">
                                    <div className="col-fluid col-max-430">
                                        <SelectEditor
                                            placeholder="Please select"
                                            options={CountryCodeMap.getArray(CountryCode.GBR)}
                                            getText={(v) => CountryCodeMap.getString(v)}
                                            label="Country of nationality"
                                            onChange={(v) => {
                                                this.loadMifirCountryIdentifier(v);
                                                const changes = [...data];
                                                changes[index].Country = v;
                                                changes[index].PrimaryCitizenship = null;
                                                changes[index].NationalIdentifier = null;
                                                changes[index].PassportNumber = null;
                                                if (v != CountryCode.GBR) {
                                                    changes[index].TaxReference = null;
                                                }

                                                onChange(changes);
                                            }}
                                            required={"Please select country of nationality"}
                                            selected={nationality.Country || null}
                                            disabled={disableTax}
                                            validationRules={[
                                                {
                                                    errorMessage: "Cannot select one country twice",
                                                    validator: (v) => {
                                                        return (
                                                            data.find((entry, i) => {
                                                                return i !== index && entry.Country === v;
                                                            }) === undefined
                                                        );
                                                    },
                                                },
                                            ]}
                                        />
                                    </div>
                                    {disableTax == false && (
                                        <>
                                            {index == 0 ? (
                                                <div className="col-auto pt-38">
                                                    {data.length < 6 && (
                                                        <button
                                                            className="add-button"
                                                            onClick={() => {
                                                                this.addCountryComponent();
                                                            }}
                                                        >
                                                            <span className="icon" aria-hidden="true" />
                                                            <span className="text">{"Add another nationality"}</span>
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    className="hyperlink"
                                                    onClick={() => {
                                                        this.removeCountryComponent(index);
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="row">
                                    <div className="col-fluid">
                                        <RadiosEditor
                                            cssWrapper="rg-inline mb-16"
                                            label={"Client primary citizenship"}
                                            selected={nationality.PrimaryCitizenship}
                                            options={[true, false]}
                                            getText={(v) => (v ? "Yes" : "No")}
                                            required={"Please select"}
                                            alwaysValidateOnUpdate={false}
                                            disabled={disableTax}
                                            onChange={(v) => {
                                                let changes = [...data];
                                                if (v) {
                                                    changes = changes.map((entry) => {
                                                        if (
                                                            entry.PrimaryCitizenship &&
                                                            nationality.Country !== entry.Country
                                                        ) {
                                                            entry.PrimaryCitizenship = false;
                                                        }
                                                        return entry;
                                                    });
                                                }
                                                changes[index].PrimaryCitizenship = v;
                                                onChange(changes);
                                            }}
                                        />
                                    </div>
                                </div>

                                {nationality.Country != null &&
                                    !this.countryHasTaxReferenceIdentifier(isTaxResident, nationality.Country) &&
                                    (this.props.isPoa || nationality.Country !== CountryCode.GBR) && (
                                        /* don't display if client is a tax resident in this country and this country has a Tax Reference */
                                        <div className="row">
                                            <div className="col-fluid col-max-430">
                                                <NationalIdentifierPair
                                                    data={nationality}
                                                    onChange={(v, r) => {
                                                        const changes = [...data];
                                                        changes[index] = r;
                                                        onChange(changes);
                                                    }}
                                                    disabled={disableTax}
                                                    onRef={(ref) => this.appendChild(ref)}
                                                    mifirCountryIdentifier={
                                                        this.state.mifirCountryIdentifiers.has(nationality.Country)
                                                            ? this.state.mifirCountryIdentifiers
                                                                  .get(nationality.Country)
                                                                  .filter(
                                                                      (ele) =>
                                                                          !(
                                                                              isTaxResident &&
                                                                              ele.IdentifierType ==
                                                                                  MifirIdentifierType.TaxNumber
                                                                          )
                                                                  )
                                                            : []
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )}
                            </div>
                        );
                    })}
            </>
        );
    }

    loadMifirCountryIdentifier(countryCode: CountryCode) {
        const { mifirCountryIdentifiers } = this.state;
        if (!mifirCountryIdentifiers.has(countryCode)) {
            new MifirCountryIdentifierClient().get(countryCode).onSuccess((data) => {
                //Concat is not eligible in new business according to old wizard logic - account info step
                mifirCountryIdentifiers.set(countryCode, data);
                this.setState({ mifirCountryIdentifiers: mifirCountryIdentifiers });
            });
        }
    }
}
